import { TOrderBy } from "@/services/requests/Recommendation/types/template8";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type TOrderByOptions = TOrderBy['options'][0];
type TOption = TOrderByOptions & {
  index: number;
}

type TProps = {
  label?: string;
  options: TOption[];
  selected?: TOption;
  onSelect?: (option: TOption) => void;
};

export default function Dropdown({ label, options, selected, onSelect }: TProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const PADDING = 12;

  useEffect(() => {
    const onDoc = (e: Event) => {
      const target = e.target as Node;
      if (
        !triggerRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const computePosition = (padding = PADDING) => {
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const availableWidth = Math.max(0, viewportWidth - 2 * padding);

    const trigRect = trigger.getBoundingClientRect();
    const desiredTop = Math.round(trigRect.bottom);
    let desiredLeft = Math.round(trigRect.left);

    menu.style.maxWidth = "";
    menu.style.width = "";
    menu.style.overflow = "";

    const menuRect = menu.getBoundingClientRect();
    let menuWidth = menuRect.width || 0;

    const style: React.CSSProperties = { position: "fixed", top: `${desiredTop}px` };

    if (menuWidth > availableWidth) {
      style.width = `${availableWidth}px`;
      style.maxWidth = `${availableWidth}px`;
      style.overflow = "auto";
      menuWidth = availableWidth;
    }

    const minLeft = padding;
    const maxLeft = Math.max(padding, viewportWidth - padding - menuWidth);
    let finalLeft = Math.min(Math.max(desiredLeft, minLeft), maxLeft);

    style.left = `${Math.round(finalLeft)}px`;

    const menuHeight = menu.getBoundingClientRect().height;
    const bottomOverflow = desiredTop + menuHeight - viewportHeight + padding;
    if (bottomOverflow > 0) {
      const altTop = trigRect.top - menuHeight;
      if (altTop >= padding) {
        style.top = `${Math.round(altTop)}px`;
      } else {
        style.top = `${Math.max(padding, viewportHeight - padding - menuHeight)}px`;
      }
    }

    setMenuStyle(style);
  };

  useEffect(() => {
    if (!open) {
      setMenuStyle({});
      return;
    }

    let raf = 0;
    const adjust = () => computePosition(PADDING);
    raf = requestAnimationFrame(adjust);

    const onWin = () => computePosition(PADDING);
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
    };
  }, [open]);

  const handleSelect = (option: TOption) => {
    onSelect?.(option);
    setOpen(false);
  };

  const menu = open ? createPortal(
    <div
      ref={menuRef}
      className="vitrine-template8__dropdown__menu"
      role="menu"
      style={menuStyle}
    >
      {options.map((opt, i) => (
        <button
          key={i}
          className={`vitrine-template8__dropdown__item ${selected?.index === i ? "selected" : ""}`}
          role="menuitem"
          onClick={() => handleSelect(opt)}
          type="button"
        >
          <div className="vitrine-template8__dropdown__item__label">
            <tgr-text-label size="md-2">{opt.title}</tgr-text-label>
          </div>
          {selected?.index === i && (
            <tgr-icon size="sm" data-testid="tgr-popover-menu-item__icon-done" icon="done"></tgr-icon>
          )}
        </button>
      ))}
    </div>,
    document.body
  ) : null;

  return (
    <div className="vitrine-template8__dropdown">
      <button
        ref={triggerRef}
        className="vitrine-template8__dropdown__trigger"
        onClick={() => setOpen((s) => !s)}
        aria-expanded={open}
        type="button"
      >
        <tgr-text-label size="md-1">{label || selected?.title || 'Selecione uma opção'}</tgr-text-label>
        <tgr-icon size="sm" icon="expand-more"></tgr-icon>
      </button>
      {menu}
    </div>
  );
}
